import React from 'react';
import { Paper, Typography, Box, IconButton, Tooltip } from '@mui/material';
import Grid from '@mui/material/Grid2';
import NorthIcon from '@mui/icons-material/North';
import SouthIcon from '@mui/icons-material/South';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { formatEuro } from '../../functions/formatNumber';
import { colors, getColorForAmount } from '../../functions/colors';

const getDynamicTitle = (baseTitle, viewMode) => {
  if (viewMode === 'actual') return baseTitle;

  const replacements = {
    "Saldo Global": { estalvi: "Saldo Estalvi Global", inversiones: "Inversions Globals" },
    "Mitja Anual": { estalvi: "Mitja Estalvi Anual", inversiones: "Mitja Inversions Anual" },
    "Saldo Anual": { estalvi: "Saldo Estalvi Anual", inversiones: "Inversions Anuals" },
    "Dif. any anterior": { estalvi: "Dif. Estalvi any ant.", inversiones: "Dif. Inversions any ant." },
    "Mitja Mensual": { estalvi: "Mitja Estalvi Mensual", inversiones: "Mitja Inversions Mensual" },
    "Desviació Mitja Anual": { estalvi: "Desv. Mitja Estalvi Anual", inversiones: "Desv. Mitja Inversions Anual" },
    "Saldo Mensual": { estalvi: "Saldo Estalvi Mensual", inversiones: "Inversions Mensuals" },
    "Mitja Diari": { estalvi: "Mitja Estalvi Diari", inversiones: "Mitja Inversions Diari" },
    "Desviació Mitja Mensual": { estalvi: "Desv. Mitja Estalvi Mensual", inversiones: "Desv. Mitja Inversions Mensual" },
    "Saldo Concepte Mensual": { estalvi: "Saldo Estalvi Concepte Mensual", inversiones: "Inversions Concepte Mensual" },
    "Saldo Concepte Global": { estalvi: "Saldo Estalvi Concepte Global", inversiones: "Inversions Concepte Global" },
    "Saldo Concepte Anual": { estalvi: "Saldo Estalvi Concepte Anual", inversiones: "Inversions Concepte Anual" },
    "Saldo Global Mensual": { estalvi: "Saldo Estalvi Global Mensual", inversiones: "Inversions Globals Mensuals" },
    "Saldo Global Tipus Moviment": { estalvi: "Saldo Estalvi Global Tipus Moviment", inversiones: "Inversions Globals Tipus Moviment" }
  };

  return replacements[baseTitle]?.[viewMode] || baseTitle;
};

const getTooltips = (baseTitle, viewMode) => {
  if (viewMode === 'inversiones') {
    let saldo = "Balanç net d'inversions (Desinversions + Inversions)";
    let ingressos = "Desinversions: total recuperat / vendes d'actius d'inversió";
    let despeses = "Inversions: total aportat / compres d'actius d'inversió";

    if (baseTitle === "Saldo Global") {
      saldo = "Rendiment / balanç net d'inversions acumulat històric";
    } else if (baseTitle === "Saldo Anual") {
      saldo = "Balanç net d'inversions de l'any (Desinversions + Inversions)";
    } else if (baseTitle === "Saldo Mensual") {
      saldo = "Balanç net d'inversions del mes (Desinversions + Inversions)";
    } else if (baseTitle === "Mitja Anual") {
      saldo = "Mitjana anual del balanç net d'inversions";
      ingressos = "Mitjana anual de desinversions";
      despeses = "Mitjana anual d'inversions";
    } else if (baseTitle === "Mitja Mensual") {
      saldo = "Mitjana mensual del balanç net d'inversions";
      ingressos = "Mitjana mensual de desinversions";
      despeses = "Mitjana mensual d'inversions";
    } else if (baseTitle === "Mitja Diari") {
      saldo = "Mitjana diària del balanç net d'inversions";
      ingressos = "Mitjana diària de desinversions";
      despeses = "Mitjana diària d'inversions";
    } else if (baseTitle === "Dif. any anterior") {
      saldo = "Diferència en balanç d'inversions respecte al mateix període de l'any anterior";
      ingressos = "Diferència de desinversions respecte a l'any anterior";
      despeses = "Diferència d'inversions respecte a l'any anterior";
    } else if (baseTitle.includes("Desviació")) {
      saldo = "Desviació del balanç d'inversions respecte a la mitjana";
      ingressos = "Desviació de desinversions respecte a la mitjana";
      despeses = "Desviació d'inversions respecte a la mitjana";
    } else if (baseTitle.includes("Concepte")) {
      saldo = "Balanç net del concepte d'inversió";
      ingressos = "Desinversions del concepte";
      despeses = "Inversions del concepte";
    }

    return { saldo, ingressos, despeses };
  }

  if (viewMode === 'estalvi') {
    let saldo = "Saldo d'estalvi (Ingressos ordinaris - Despeses ordinàries, excloent inversions)";
    let ingressos = "Ingressos ordinaris (nòmines i altres entrades, excloent desinversions)";
    let despeses = "Despeses ordinàries (factures i compres del dia a dia, excloent inversions)";

    if (baseTitle === "Saldo Global") {
      saldo = "Saldo d'estalvi acumulat històric (Ingressos ordinaris - Despeses ordinàries)";
    } else if (baseTitle === "Saldo Anual") {
      saldo = "Saldo d'estalvi de l'any (Ingressos ordinaris - Despeses ordinàries)";
    } else if (baseTitle === "Saldo Mensual") {
      saldo = "Saldo d'estalvi del mes (Ingressos ordinaris - Despeses ordinàries)";
    } else if (baseTitle === "Mitja Anual") {
      saldo = "Mitjana anual de saldo d'estalvi";
      ingressos = "Mitjana anual d'ingressos ordinaris";
      despeses = "Mitjana anual de despeses ordinàries";
    } else if (baseTitle === "Mitja Mensual") {
      saldo = "Mitjana mensual de saldo d'estalvi";
      ingressos = "Mitjana mensual d'ingressos ordinaris";
      despeses = "Mitjana mensual de despeses ordinàries";
    } else if (baseTitle === "Mitja Diari") {
      saldo = "Mitjana diària de saldo d'estalvi";
      ingressos = "Mitjana diària d'ingressos ordinaris";
      despeses = "Mitjana diària de despeses ordinàries";
    } else if (baseTitle === "Dif. any anterior") {
      saldo = "Diferència de saldo d'estalvi respecte a l'any anterior";
      ingressos = "Diferència d'ingressos ordinaris respecte a l'any anterior";
      despeses = "Diferència de despeses ordinàries respecte a l'any anterior";
    } else if (baseTitle.includes("Desviació")) {
      saldo = "Desviació del saldo d'estalvi respecte a la mitjana";
      ingressos = "Desviació d'ingressos ordinaris respecte a la mitjana";
      despeses = "Desviació de despeses ordinàries respecte a la mitjana";
    } else if (baseTitle.includes("Concepte")) {
      saldo = "Saldo del concepte (Ingressos - Despeses)";
      ingressos = "Ingressos del concepte";
      despeses = "Despeses del concepte";
    }

    return { saldo, ingressos, despeses };
  }

  // viewMode === 'actual' (Saldo total)
  let saldo = "Saldo total (Ingressos totals - Despeses totals)";
  let ingressos = "Ingressos totals (Ingressos ordinaris + Desinversions)";
  let despeses = "Despeses totals (Despeses ordinàries + Inversions)";

  if (baseTitle === "Saldo Global") {
    saldo = "Saldo total acumulat històric (Ingressos totals - Despeses totals)";
  } else if (baseTitle === "Saldo Anual") {
    saldo = "Saldo total de l'any (Ingressos totals - Despeses totals)";
  } else if (baseTitle === "Saldo Mensual") {
    saldo = "Saldo total del mes (Ingressos totals - Despeses totals)";
  } else if (baseTitle === "Mitja Anual") {
    saldo = "Mitjana anual de saldo total";
    ingressos = "Mitjana anual d'ingressos totals";
    despeses = "Mitjana anual de despeses totals";
  } else if (baseTitle === "Mitja Mensual") {
    saldo = "Mitjana mensual de saldo total";
    ingressos = "Mitjana mensual d'ingressos totals";
    despeses = "Mitjana mensual de despeses totals";
  } else if (baseTitle === "Mitja Diari") {
    saldo = "Mitjana diària de saldo total";
    ingressos = "Mitjana diària d'ingressos totals";
    despeses = "Mitjana diària de despeses totals";
  } else if (baseTitle === "Dif. any anterior") {
    saldo = "Diferència de saldo total respecte a l'any anterior";
    ingressos = "Diferència d'ingressos totals respecte a l'any anterior";
    despeses = "Diferència de despeses totals respecte a l'any anterior";
  } else if (baseTitle.includes("Desviació")) {
    saldo = "Desviació de saldo total respecte a la mitjana";
    ingressos = "Desviació d'ingressos totals respecte a la mitjana";
    despeses = "Desviació de despeses totals respecte a la mitjana";
  } else if (baseTitle.includes("Concepte")) {
    saldo = "Saldo total del concepte";
    ingressos = "Ingressos totals del concepte";
    despeses = "Despeses totals del concepte";
  }

  return { saldo, ingressos, despeses };
};

const Saldo = ({ 
  titol, 
  saldo, 
  ingressos, 
  despeses, 
  size = { xs: 12, sm: 6, md: 3 }, 
  mode = 'estalvi', 
  onToggleMode, 
  showToggleIcon = false 
}) => {
  const colorSaldo = getColorForAmount(saldo, mode === 'inversiones');
  const hasSubBalances = ingressos !== undefined && despeses !== undefined && ingressos !== null && despeses !== null;
  const tooltips = getTooltips(titol, mode);

  return (
    <Grid size={size}>
      <Paper sx={{ padding: '10px', textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', position: 'relative', zIndex: 2 }}>
          <Typography variant="h6">{ getDynamicTitle(titol, mode) }</Typography>
          {showToggleIcon && onToggleMode && (
            <Tooltip title={`Canviar vista de saldo (actual: ${mode === 'estalvi' ? 'Saldo Estalvi' : mode === 'inversiones' ? 'Inversions' : 'Saldo Total'})`} arrow placement="top" enterDelay={200}>
              <IconButton onClick={onToggleMode} size="small" sx={{ position: 'relative', zIndex: 3 }}>
                <SwapHorizIcon sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Tooltip 
            title={tooltips.saldo} 
            arrow 
            placement="bottom" 
            enterDelay={250}
            disableInteractive
            slotProps={{
              popper: {
                sx: { pointerEvents: 'none' }
              }
            }}
          >
            <Typography variant="h4" color={colorSaldo} sx={{ cursor: 'help', display: 'inline-block' }}>
              { formatEuro(saldo) }
            </Typography>
          </Tooltip>
        </Box>

        {/* Ingresos y Gastos en una línea con separador */}
        {hasSubBalances && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '10px' }}>
            {/* Ingresos a la izquierda */}
            <Tooltip 
              title={tooltips.ingressos} 
              arrow 
              placement="bottom" 
              enterDelay={250}
              disableInteractive
              slotProps={{
                popper: {
                  sx: { pointerEvents: 'none' }
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
                <NorthIcon sx={{ color: mode === 'inversiones' ? colors.investmentPos : "#4CAF50", fontSize: '15px', marginBottom: '3px' }}/>
                <Typography variant="subtitle1" sx={{ textAlign: 'left', color: mode === 'inversiones' ? colors.investmentPos : "inherit" }}>
                  {formatEuro(ingressos)}
                </Typography>
              </Box>
            </Tooltip>

            {/* Gastos a la derecha */}
            <Tooltip 
              title={tooltips.despeses} 
              arrow 
              placement="bottom" 
              enterDelay={250}
              disableInteractive
              slotProps={{
                popper: {
                  sx: { pointerEvents: 'none' }
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
                <SouthIcon sx={{ color: mode === 'inversiones' ? colors.investmentNeg : "#C2185B", fontSize: '15px', marginTop: '6px' }}/>
                <Typography variant="subtitle1" sx={{ textAlign: 'right', color: mode === 'inversiones' ? colors.investmentNeg : "inherit" }}>
                  {formatEuro(despeses)}
                </Typography>
              </Box>
            </Tooltip>
          </Box>      
        )}
      </Paper>
    </Grid>
  );
};

export default Saldo;
